import { useMemo } from 'react';
import { useStoreActions } from '~/contexts';
import { useQueryState } from '~/hooks/reactQuery/useQueryState';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import * as serviceTranslator from '~/models/service/translator';
import { QueryKeys } from '../queryKeys';
const buildBillingScheduleQueryParams = (params)=>{
    const queryParams = {};
    if ((params === null || params === void 0 ? void 0 : params.selectedOptionalServices) && params.selectedOptionalServices.length > 0) {
        queryParams.selectedOptionalServices = params.selectedOptionalServices.join(',');
    }
    if (params === null || params === void 0 ? void 0 : params.selectedBundleId) {
        queryParams.selectedBundleId = params.selectedBundleId;
    }
    return Object.keys(queryParams).length > 0 ? queryParams : undefined;
};
const servicesToDTO = (services)=>{
    return services.map((service)=>serviceTranslator.toDTO(service));
};
const serviceBundlesToDTO = (serviceBundles)=>{
    if (!serviceBundles || serviceBundles.bundles.length === 0) {
        return null;
    }
    return {
        bundles: serviceBundles.bundles.map((bundle)=>({
                id: bundle.id,
                name: bundle.name,
                services: servicesToDTO(bundle.services)
            }))
    };
};
export const useBillingSchedule = (param)=>{
    let { services, serviceBundles, effectiveDate, selectedOptionalServices, selectedBundleId, entityContext } = param;
    const { api } = useStoreActions();
    const { BILLING_SCHEDULE_NEW_UI, BILLING_SCHEDULE_GET_ALL_ENDPOINTS } = useFeatureFlags();
    const servicesDTO = useMemo(()=>servicesToDTO(services), [
        services
    ]);
    const serviceBundlesDTO = useMemo(()=>serviceBundlesToDTO(serviceBundles), [
        serviceBundles
    ]);
    const queryKey = useMemo(()=>{
        if (BILLING_SCHEDULE_GET_ALL_ENDPOINTS && entityContext) {
            return [
                QueryKeys.BillingSchedule,
                entityContext.source,
                entityContext.id,
                (selectedOptionalServices === null || selectedOptionalServices === void 0 ? void 0 : selectedOptionalServices.join(',')) || '',
                selectedBundleId || ''
            ];
        }
        return [
            QueryKeys.BillingSchedule,
            JSON.stringify(servicesDTO),
            JSON.stringify(serviceBundlesDTO),
            JSON.stringify(effectiveDate),
            (selectedOptionalServices === null || selectedOptionalServices === void 0 ? void 0 : selectedOptionalServices.join(',')) || '',
            selectedBundleId || ''
        ];
    }, [
        BILLING_SCHEDULE_GET_ALL_ENDPOINTS,
        entityContext,
        servicesDTO,
        serviceBundlesDTO,
        effectiveDate,
        selectedOptionalServices,
        selectedBundleId
    ]);
    const { data: billingSchedule, isLoading, isBackgroundLoading, isFetching, error, refresh } = useQueryState({
        enabled: BILLING_SCHEDULE_NEW_UI,
        queryKey,
        queryFn: ()=>{
            const queryParams = buildBillingScheduleQueryParams({
                selectedOptionalServices,
                selectedBundleId
            });
            if (BILLING_SCHEDULE_GET_ALL_ENDPOINTS && entityContext) {
                switch(entityContext.source){
                    case 'relationship':
                        return api.relationship.getBillingSchedule(entityContext.id, queryParams);
                    case 'draft':
                        return api.draft.getBillingSchedule(entityContext.id, queryParams);
                    case 'proposalTemplate':
                        return api.proposalTemplates.getBillingSchedule(entityContext.id, queryParams);
                }
            }
            return api.billing.getBillingSchedule({
                services: servicesDTO,
                serviceBundles: serviceBundlesDTO,
                effectiveDate: effectiveDate !== null && effectiveDate !== void 0 ? effectiveDate : null
            }, queryParams);
        },
        refreshOnHookMount: true,
        keepPreviousData: false
    });
    return {
        billingSchedule,
        isLoading,
        isBackgroundLoading,
        isFetching,
        error,
        refresh
    };
};
